"use client";

import { InfoIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../../lib";
import {
	CONTENT_TOOLTIP_SHORT,
	ContentTooltip,
} from "../design-system/tooltip";
import { StatusCallout } from "../status-callout";
import { fieldErrorId } from "./field-a11y";

export type FieldLayoutHint = "narrow" | "wide" | "portal";

export type FieldLabelVariant = "default" | "compact";

export interface FieldShellProps {
	id?: string;
	label?: ReactNode;
	description?: string;
	/**
	 * Info-circle tooltip next to the label (audio-sheet / Create settings pattern).
	 * Prefer this over a tall `description` block when space is tight.
	 */
	labelInfo?: ReactNode;
	labelInfoAriaLabel?: string;
	/**
	 * Visible validation / atom error. Strings render as compact StatusCallout error.
	 * ReactNode is rendered as-is inside `#${id}-error`.
	 */
	error?: ReactNode;
	/**
	 * When true, always render the error row (`min-h-5`) even if `error` is empty
	 * (layout-stable forms e.g. waitlist).
	 */
	reserveErrorSpace?: boolean;
	errorClassName?: string;
	className?: string;
	layoutHint?: FieldLayoutHint;
	/** Accessible description for mixed / helper text (aria-describedby). */
	describedById?: string;
	/**
	 * `compact` matches Create settings chrome (`text-xs` + min-h-5 label row).
	 * `default` is form-scale (`text-sm`).
	 */
	labelVariant?: FieldLabelVariant;
	/** Optional trailing control on the label row (e.g. Reset link). */
	labelTrailing?: ReactNode;
	/**
	 * `full` (default): block `w-full` for form layouts.
	 * `intrinsic`: content-sized (`w-max max-w-full shrink-0`) for toolbars /
	 * chrome pickers — avoids Flex/grid collapse of selects and switches.
	 */
	widthMode?: "full" | "intrinsic";
	children: ReactNode;
}

function hasErrorContent(error?: ReactNode): boolean {
	if (error == null || error === false) {
		return false;
	}
	if (typeof error === "string") {
		return error.length > 0;
	}
	return true;
}

/**
 * Shared field chrome. Uses named container `@container/field` so children can
 * switch narrow | wide with `@field:` variants (e.g. `@field:[20rem]:…`).
 */
export function FieldShell({
	id,
	label,
	description,
	labelInfo,
	labelInfoAriaLabel,
	error,
	reserveErrorSpace = false,
	errorClassName,
	className,
	layoutHint,
	describedById,
	labelVariant = "default",
	labelTrailing,
	widthMode = "full",
	children,
}: FieldShellProps) {
	const labelId = id ? `${id}-label` : undefined;
	const descriptionId = id ? `${id}-description` : undefined;
	const showError = hasErrorContent(error);
	const errorId =
		id && (showError || reserveErrorSpace) ? fieldErrorId(id) : undefined;
	const describedBy = [
		describedById,
		description ? descriptionId : null,
		showError ? errorId : null,
	]
		.filter(Boolean)
		.join(" ");
	const isCompact = labelVariant === "compact";
	const labelAria =
		typeof label === "string"
			? label
			: labelInfoAriaLabel?.replace(/^About\s+/i, "") || "field";

	const labelEl = label ? (
		<label
			id={labelId}
			htmlFor={id}
			className={cn(
				"min-w-0 font-medium text-foreground",
				isCompact ? "text-xs" : "text-sm",
			)}
		>
			{label}
		</label>
	) : null;

	const infoEl =
		labelInfo != null ? (
			<ContentTooltip
				{...CONTENT_TOOLTIP_SHORT}
				side="bottom"
				contentClassName="max-w-xs text-xs leading-snug"
				content={labelInfo}
			>
				<span
					role="button"
					tabIndex={0}
					className="relative z-[1] inline-flex size-4 shrink-0 cursor-help items-center justify-center rounded-sm p-0.5 text-muted-foreground transition-colors hover:text-foreground [transform:translate3d(0,0,0)] [backface-visibility:hidden]"
					aria-label={labelInfoAriaLabel ?? `About ${labelAria}`}
					onClick={(event) => {
						event.preventDefault();
						event.stopPropagation();
					}}
					onPointerDown={(event) => {
						event.preventDefault();
						event.stopPropagation();
					}}
					onKeyDown={(event) => {
						if (event.key === "Enter" || event.key === " ") {
							event.preventDefault();
							event.stopPropagation();
						}
					}}
				>
					<InfoIcon className="size-3.5" aria-hidden />
				</span>
			</ContentTooltip>
		) : null;

	const errorContent =
		typeof error === "string" ? (
			<StatusCallout variant="error" size="compact">
				{error}
			</StatusCallout>
		) : (
			error
		);

	return (
		<div
			data-field-shell
			data-layout-hint={layoutHint ?? undefined}
			data-label-variant={labelVariant}
			className={cn(
				"@container/field",
				widthMode === "intrinsic"
					? "inline-flex w-max max-w-full shrink-0 flex-col"
					: "w-full",
				layoutHint === "narrow" && "max-w-sm",
				layoutHint === "wide" && "max-w-3xl",
				className,
			)}
			data-width-mode={widthMode}
		>
			{labelEl || labelTrailing || infoEl ? (
				<div
					className={cn(
						"flex w-full min-w-0 items-center justify-between gap-2",
						isCompact ? "mb-1.5 min-h-5" : "mb-1.5",
					)}
				>
					<div className="inline-flex min-w-0 items-center gap-1">
						{labelEl}
						{infoEl}
					</div>
					{labelTrailing ? (
						<div className="shrink-0 self-center">{labelTrailing}</div>
					) : null}
				</div>
			) : null}
			{description ? (
				<p
					id={descriptionId}
					className="mb-2 text-xs text-muted-foreground"
				>
					{description}
				</p>
			) : null}
			<div
				{...(describedBy
					? { "aria-describedby": describedBy }
					: {})}
			>
				{children}
			</div>
			{errorId ? (
				<div
					id={errorId}
					role={showError ? "alert" : undefined}
					aria-hidden={!showError}
					className={cn(
						"mt-1.5 min-h-5",
						errorClassName,
						!showError && "opacity-0",
					)}
				>
					{showError ? errorContent : null}
				</div>
			) : null}
		</div>
	);
}

export const FIELD_CONTAINER_CLASS = "@container/field";
